import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { validateChartRequest } from "@/lib/chart-validators";

interface ChartPreviewInlineProps {
  chart: any;
  onExpand: () => void;
}

export function ChartPreviewInline({ chart, onExpand }: ChartPreviewInlineProps) {
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Generate preview when chart changes
  useEffect(() => {
    if (chart) {
      generatePreview();
    }
    return () => {
      // Cleanup: revoke object URL
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [chart]);

  const generatePreview = async () => {
    if (!chart) return;

    setLoading(true);
    setError(null);

    try {
      // Build chart data from configuration
      const chartData = buildChartData(chart);

      console.log("🎨 Generating chart preview inline:", {
        type: chart.chart_type,
        data: chartData,
      });

      // Preparar el request completo
      let chartRequest: any;

      // Para bar-referral y bar, buildChartData retorna {data, options}
      if (chart.chart_type === "bar-referral" || (chart.chart_type === "bar" && typeof chartData === 'object' && 'data' in chartData)) {
        chartRequest = {
          type: chart.chart_type,
          data: (chartData as any).data,
          options: (chartData as any).options || chart.chart_config.options || {},
        };
      } else {
        // Para otros tipos (radial, pie, etc), chartData ya es el array directo
        chartRequest = {
          type: chart.chart_type,
          data: chartData,
          options: chart.chart_config.options || {},
        };
      }

      // VALIDAR ANTES DE ENVIAR
      const validation = validateChartRequest(chartRequest);

      if (!validation.success) {
        const errorMsg = `Errores de validación: ${validation.errors.map(e => e.message).join(', ')}`;
        setError(errorMsg);
        console.error("❌ Validación fallida:", validation.errors);
        return;
      }

      console.log("✅ Validación exitosa (inline)");

      // Call generate-chart edge function using fetch to handle binary properly
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-chart`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify(validation.data),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error ${response.status}: ${errorText}`);
      }

      // Get the PNG blob from response
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setImageUrl(url);
    } catch (err: any) {
      console.error("Error generating chart preview:", err);
      setError(err.message || "Error al generar vista previa");
      toast.error("Error al generar vista previa del gráfico");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to resolve a value for preview
  const resolveValueForPreview = (valueConfig: any): number => {
    // If it's a direct number/string (legacy format)
    if (typeof valueConfig === 'number') return Math.round(valueConfig * 100) / 100;
    if (typeof valueConfig === 'string') {
      const parsed = parseFloat(valueConfig) || 0;
      return Math.round(parsed * 100) / 100;
    }

    // If it's the new format {type, value}
    if (valueConfig && typeof valueConfig === 'object' && 'type' in valueConfig) {
      if (valueConfig.type === 'number') {
        const parsed = parseFloat(valueConfig.value) || 0;
        return Math.round(parsed * 100) / 100;
      }
      // For variables/calculations, use random sample for preview (with 2 decimals)
      const randomValue = 5 + Math.random() * 5;
      return Math.round(randomValue * 100) / 100;
    }

    return 0;
  };

  // Build chart data from configuration (same logic as ChartPreviewDialog)
  const buildChartData = (chart: any) => {
    const config = chart.chart_config || {};
    const colors = config.colors || [];
    const dataParams = config.data_params || [];
    const labels = config.labels || {};

    console.log("📊 Building chart data (inline):", { config, dataParams });

    // Handle radial charts
    if (chart.chart_type === "radial") {
      const data = dataParams.map((category: any) => {
        // If already in new format with dimensions
        if (category.dimensions && Array.isArray(category.dimensions)) {
          return {
            name: category.name,
            color: category.color,
            dimensions: category.dimensions.map((dim: any) => ({
              name: dim.name,
              barValue: resolveValueForPreview(dim.barValue),
              lineValue: resolveValueForPreview(dim.lineValue),
            })),
          };
        }

        // If old format {label, value, color}, convert to new format
        if (category.label !== undefined) {
          return {
            name: category.label,
            color: category.color || "#000000",
            dimensions: [{
              name: "Valor",
              barValue: parseFloat(category.value) || 0,
              lineValue: parseFloat(category.value) || 0,
            }],
          };
        }

        return category;
      });

      return data;
    }

    // Build data based on chart type
    if (chart.chart_type === "bar-referral") {
      // For bar-referral, cada parámetro contiene userValue (value) y referralValue (value2)
      const data = dataParams.map((param: any, index: number) => {
        const userValue = param.type === "number"
          ? parseFloat(param.value) || 0
          : Math.floor(3 + Math.random() * 5); // Número entero entre 3-7

        const referralValue = param.type2 === "number"
          ? parseFloat(param.value2) || 0
          : parseFloat((3 + Math.random() * 5).toFixed(2)); // Número decimal entre 3.00-8.00 con máximo 2 decimales

        return {
          label: param.label || ` `,
          userValue,
          referralValue,
        };
      });

      return {
        data,
        options: {
          userColor: colors[0] || "#7C3AED",
          referralColor: colors[1] || "#10B981",
          userLabel: labels.userLabel || "Usuario",
          referralLabel: labels.referralLabel || "Referido",
        },
      };
    }

    // For other chart types
    const data = dataParams.map((param: any, index: number) => {
      // Use resolveValueForPreview to handle both old and new format
      const value = resolveValueForPreview(param.value);

      const item: any = {
        label: param.label || `Item ${index + 1}`,
        value: value,
      };

      // Add color if available
      if (colors[index]) {
        item.color = colors[index];
      }

      return item;
    });

    // Add specific options for bar chart
    if (chart.chart_type === "bar" && colors.length > 0) {
      return {
        data,
        options: {
          barColor: colors[0],
          barBackgroundColor: "transparent", // Sin fondo de barra (como bar-referral)
        },
      };
    }

    return data;
  };

  return (
    <div className="relative border rounded-lg overflow-hidden bg-muted/30">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-primary" />
            <p className="text-xs text-muted-foreground">Generando...</p>
          </div>
        </div>
      )}

      {error && !loading && (
        <div className="flex items-center justify-center min-h-[150px] p-4 text-center">
          <p className="text-xs text-red-500">{error}</p>
        </div>
      )}

      {imageUrl && !loading && !error && (
        <>
          <div className="relative group">
            <img
              src={imageUrl}
              alt={chart?.name}
              className="w-full h-auto"
            />
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                size="icon"
                variant="secondary"
                onClick={onExpand}
                className="h-8 w-8"
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="p-2 text-xs text-muted-foreground bg-muted/50">
            Vista previa con valores de ejemplo
          </div>
        </>
      )}

      {!imageUrl && !loading && !error && (
        <div className="flex items-center justify-center min-h-[150px] p-4">
          <p className="text-xs text-muted-foreground">Sin vista previa</p>
        </div>
      )}
    </div>
  );
}
